import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import type { Compset } from 'frontend-utils/models';
import type { OTA_IDS } from 'frontend-utils/utils/constants/constant-values';
import generateOtaLink from 'frontend-utils/utils/url/generate-ota-link';
import HotelChannelPricingTablesCellsChannelsTableTdChannelsTableNameCell from 'lumen/components/hotel/channel-pricing/tables/cells/channels-table/td-channels-table-name-cell';
import HotelChannelPricingTablesCellsChannelsTableTdChannelsTableValueCell from 'lumen/components/hotel/channel-pricing/tables/cells/channels-table/td-channels-table-value-cell';
import type {
  Carate,
  Day,
  Period,
  PriceAction,
  Restriction,
  RestrictionRecommendation,
} from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  CHANNELS_TABLE_ROW_TYPES,
  RESTRICTION_MODEL_CONTEXTS,
} from 'lumen/utils/constants/channel-pricing-constants';
import { DAY_TABLE_SIZES } from 'lumen/utils/constants/general-constants';

import styles from './channels-table.module.css';

export interface TdChannelsTableColumnSignature extends CustomEmberTableColumn {
  caratesByHotelInfoId: Record<string, Carate>;
  day: Day;
  daysWithActivePricePush: string[];
  daysWithFailedPricePush: string[];
  daysWithFailedRestrictionPush: Record<string, string[]>;
  hotelLevelRestrictions: Restriction[];
  id: string;
  isPastDay: boolean;
  otaRedirectLink: string;
  priceAction?: PriceAction;
  restrictions: Restriction[];
  restrictionsRecommendations: RestrictionRecommendation[];
  selectedChannelId: (typeof OTA_IDS)[keyof typeof OTA_IDS];
}

export interface TdChannelsTableRowSignature extends CustomEmberTableRow {
  hotelInfoId: string;
  type: (typeof CHANNELS_TABLE_ROW_TYPES)[keyof typeof CHANNELS_TABLE_ROW_TYPES];
}

const ROW_PROPERTIES = {
  NAME: 'name',
  TYPE: 'type',
} as const;

interface HotelChannelPricingTablesChannelsTableSignature {
  Args: {
    data: Period[];
    daysWithActivePricePush: string[];
    daysWithFailedPricePush: string[];
    daysWithFailedRestrictionPush: Record<string, string[]>;
    isLoading: boolean;
    selectedChannelId: (typeof OTA_IDS)[keyof typeof OTA_IDS];
  };
}

export default class HotelChannelPricingTablesChannelsTable extends Component<HotelChannelPricingTablesChannelsTableSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  get columns(): TdChannelsTableColumnSignature[] {
    const columns: Partial<TdChannelsTableColumnSignature>[] = [
      {
        name: this.intl.t('channel_pricing.restrictions'),
        valuePath: 'name',
        textAlign: 'left',
        maxWidth: DAY_TABLE_SIZES.NAME_MAX_WIDTH,
        tdClassNames: 'ic-product-tour-49521458-sd-channels-table-name-cell',
        tdComponent:
          HotelChannelPricingTablesCellsChannelsTableTdChannelsTableNameCell,
      },
    ];

    this.args.data.forEach((period) => {
      // Extra carate filter because we have multiple carates for the own hotel, and we only need to display the base room price in the channels table
      // This is because we also fetch the carates for all own hotel rooms for the PMS table on this page
      const relevantCarates = period.carates.filter((carate) => {
        // Make sure we only use carates linked to the correct channel
        if (carate.ota !== this.args.selectedChannelId) {
          return false;
        }

        if (
          carate.hotelinfo.id === this.hotelAndMarketService.hotel.hotelinfo.id
        ) {
          return carate.baseRoom;
        }

        return true;
      });

      const caratesByHotelInfoId: Record<string, Carate> =
        relevantCarates.reduce((obj: Record<string, Carate>, item) => {
          const hotelInfoId = item.hotelinfo.id!;
          obj[hotelInfoId] = item;
          return obj;
        }, {});

      const restrictions: Restriction[] = period.restrictions.filter(
        (restriction) => {
          return (
            restriction.context === RESTRICTION_MODEL_CONTEXTS.CHANNEL_LEVEL &&
            restriction.channelId === this.args.selectedChannelId
          );
        },
      );

      const hotelLevelRestrictions: Restriction[] = period.restrictions.filter(
        (restriction) =>
          restriction.context === RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL,
      );

      const channelLevelRestrictionRecommendations =
        period.restrictionRecommendations.filter(
          (restrictionRecommendation) =>
            restrictionRecommendation.context ===
              RESTRICTION_MODEL_CONTEXTS.CHANNEL_LEVEL &&
            restrictionRecommendation.channelId === this.args.selectedChannelId,
        );

      const otaRedirectLink = generateOtaLink(
        this.args.selectedChannelId,
        this.hotelAndMarketService.hotel?.hotelinfo?.id ?? '',
        this.hotelAndMarketService.hotel?.id ?? '',
        this.hotelAndMarketService.hotel?.isTripadvisorRebranding,
        period,
        this.hotelAndMarketService.hotel?.paRatePreference?.persons,
        'app_channel_pricing',
      );

      columns.push({
        id: period.day.id!,
        caratesByHotelInfoId,
        restrictions,
        hotelLevelRestrictions,
        restrictionsRecommendations: channelLevelRestrictionRecommendations,
        name: '',
        valuePath: '',
        width: DAY_TABLE_SIZES.VALUE_WIDTH,
        minWidth: DAY_TABLE_SIZES.VALUE_MIN_WIDTH,
        tdComponent:
          HotelChannelPricingTablesCellsChannelsTableTdChannelsTableValueCell,
        day: period.day,
        isPastDay: period.day.isPast,
        priceAction: period.priceAction,
        tdClassNames: 'channel-pricing-td-no-default-padding',
        selectedChannelId: this.args.selectedChannelId,
        daysWithActivePricePush: this.args.daysWithActivePricePush,
        daysWithFailedPricePush: this.args.daysWithFailedPricePush,
        daysWithFailedRestrictionPush: this.args.daysWithFailedRestrictionPush,
        otaRedirectLink,
      });
    });

    return columns as TdChannelsTableColumnSignature[];
  }

  get rows(): CustomEmberTableRow[] {
    const competitorChildRows = (
      this.hotelAndMarketService.primaryCompset as Compset
    ).competitors.map((competitor) => {
      return {
        [ROW_PROPERTIES.NAME]: competitor.name,
        [ROW_PROPERTIES.TYPE]: CHANNELS_TABLE_ROW_TYPES.PRICING_CHILD_ROW,
        hotelInfoId: competitor.id,
      };
    });

    const pmsRoomtypeIdToPmsNameMapping =
      this.hotelAndMarketService.hotel.pricingIntegration
        .pms_id_to_pms_room_name;
    const formattedRoomname =
      pmsRoomtypeIdToPmsNameMapping[
        this.hotelAndMarketService.hotel.pricingIntegration
          .pms_leading_room_pms_id
      ];

    const rows = [
      {
        [ROW_PROPERTIES.NAME]: this.intl.t('channel_pricing.restrictions'),
        [ROW_PROPERTIES.TYPE]: CHANNELS_TABLE_ROW_TYPES.RESTRICTION,
        className: 'channel-pricing-tr-restrictions',
      },
      {
        [ROW_PROPERTIES.NAME]: formattedRoomname,
        [ROW_PROPERTIES.TYPE]: CHANNELS_TABLE_ROW_TYPES.PRICING_PARENT_ROW,
        hotelInfoId: this.hotelAndMarketService.hotel.hotelinfo.id,
        children: competitorChildRows,
        isCollapsed: false,
      },
    ];

    return rows;
  }

  <template>
    <PrismTable
      @columns={{this.columns}}
      @fillMode="first-column"
      @isLoading={{@isLoading}}
      @nrOfEmptyRows={{this.hotelAndMarketService.primaryCompset.competitors.length}}
      @nrOfLoadingRows={{2}}
      @rows={{this.rows}}
      @widthConstraint="eq-container"
      class="{{styles.table}} {{styles.channels-table}}"
      data-test-id="channels-table"
      {{! combination of these 2 row arguments below should result in a realistic loading state }}
    />
  </template>
}
